import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Slide,
} from '@mui/material';
import {
  Verified as VerifiedIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Star as StarIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useFeedback } from '../context/FeedbackContext';

interface VerificationData {
  report_info: {
    token: string;
    generated_at: string;
    is_valid: boolean;
  };
  student_info: {
    name: string;
    student_id: string;
    class_level: string;
    school: string;
  };
  exam_info: {
    title: string;
    exam_date: string;
    exam_type: string;
    total_marks: number;
    passing_marks: number;
  };
  performance: {
    total_score: number;
    total_max_score: number;
    percentage: number;
    status: string;
    subjects: Array<{
      subject: string;
      score: number;
      max_score: number;
      percentage: number;
    }>;
  };
  verification: {
    verified_at: string;
    verification_url: string;
  };
}

const ReportVerification: React.FC = () => {
  const { reportToken } = useParams<{ reportToken: string }>();
  const navigate = useNavigate();
  const { showError } = useFeedback();
  
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manualToken, setManualToken] = useState('');

  useEffect(() => {
    if (reportToken) {
      fetchVerificationData(reportToken);
    } else {
      setLoading(false);
    }
  }, [reportToken]);

  const fetchVerificationData = async (token: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/reports/verify-report/${token}`);
      setVerificationData(response.data as VerificationData | null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify report');
      setVerificationData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerification = () => {
    if (manualToken.trim()) {
      navigate(`/verify-report/${manualToken.trim()}`);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a success notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    return status === 'Passed' ? 'success' : 'error';
  };

  // CBC Grading System
  const getCBCGrade = (percentage: number) => {
    if (percentage >= 80) return { grade: 'EE', description: 'Exceeding Expectations', color: 'success' as const };
    if (percentage >= 70) return { grade: 'ME', description: 'Meeting Expectations', color: 'primary' as const };
    if (percentage >= 60) return { grade: 'AE', description: 'Approaching Expectations', color: 'warning' as const };
    return { grade: 'BE', description: 'Below Expectations', color: 'error' as const };
  };

  const getCBCGradeIcon = (grade: string) => {
    switch (grade) {
      case 'EE': return <StarIcon />;
      case 'ME': return <CheckCircleIcon />;
      case 'AE': return <TrendingUpIcon />;
      case 'BE': return <TrendingDownIcon />;
      default: return <RemoveIcon />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Fade in timeout={500}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Fade>
        
        <Zoom in timeout={600}>
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, boxShadow: 3 }}>
            <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Report Verification
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enter a report token to verify a student report
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
              <TextField
                label="Report Token"
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                placeholder="Enter report token..."
                sx={{ minWidth: 300 }}
                onKeyPress={(e) => e.key === 'Enter' && handleManualVerification()}
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={handleManualVerification}
                disabled={!manualToken.trim()}
                startIcon={<VerifiedIcon />}
                sx={{ px: 3, py: 1.5 }}
              >
                Verify
              </Button>
            </Stack>
          </Paper>
        </Zoom>
      </Container>
    );
  }

  if (!verificationData) {
    return null;
  }

  const overallCBCGrade = getCBCGrade(verificationData.performance.percentage);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'success.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VerifiedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Report Verification
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Official verification of student academic report
              </Typography>
            </Box>
          </Box>
          
          <Chip
            icon={<CheckCircleIcon />}
            label="VERIFIED"
            color="success"
            variant="filled"
            sx={{ fontWeight: 'bold', px: 2, py: 1 }}
          />
        </Box>
      </Fade>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Top Row - Student and Exam Info */}
        <Slide direction="up" in timeout={600}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {/* Student Information */}
            <Box sx={{ flex: { xs: 1, md: 1 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">Student Information</Typography>
                  </Box>
                  
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Name</Typography>
                      <Typography variant="h6" sx={{ mt: 0.5 }}>{verificationData.student_info.name}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Student ID</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{verificationData.student_info.student_id || 'N/A'}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Class Level</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{verificationData.student_info.class_level}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">School</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{verificationData.student_info.school}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Exam Information */}
            <Box sx={{ flex: { xs: 1, md: 1 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'secondary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">Exam Information</Typography>
                  </Box>
                  
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Exam Title</Typography>
                      <Typography variant="h6" sx={{ mt: 0.5 }}>{verificationData.exam_info.title}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Exam Date</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(verificationData.exam_info.exam_date)}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Exam Type</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{verificationData.exam_info.exam_type}</Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Total Marks</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{verificationData.exam_info.total_marks}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Slide>

        {/* Performance Summary */}
        <Slide direction="up" in timeout={700}>
          <Box>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'info.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold">Performance Summary</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 4 }}>
                  <Box sx={{ flex: { xs: 1, sm: 1, md: 1 } }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="h3" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        {verificationData.performance.percentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Overall Score</Typography>
                      <Chip
                        label={verificationData.performance.status}
                        color={getStatusColor(verificationData.performance.status)}
                        size="small"
                        sx={{ mt: 1, fontWeight: 'bold' }}
                      />
                    </Paper>
                  </Box>
                  
                  <Box sx={{ flex: { xs: 1, sm: 1, md: 1 } }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="h3" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        {verificationData.performance.total_score}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Total Score</Typography>
                    </Paper>
                  </Box>
                  
                  <Box sx={{ flex: { xs: 1, sm: 1, md: 1 } }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="h3" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        {verificationData.performance.total_max_score}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Maximum Score</Typography>
                    </Paper>
                  </Box>
                  
                  <Box sx={{ flex: { xs: 1, sm: 1, md: 1 } }}>
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          icon={getCBCGradeIcon(overallCBCGrade.grade)}
                          label={overallCBCGrade.grade}
                          color={overallCBCGrade.color}
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.2rem',
                            height: '2.5rem',
                            '& .MuiChip-icon': { fontSize: '1.5rem' }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>
                        {overallCBCGrade.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        CBC Grade
                      </Typography>
                    </Paper>
                  </Box>
                </Box>

                {/* Subject Breakdown */}
                <Typography variant="h6" sx={{ mb: 3 }} fontWeight="bold">Subject Breakdown</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Subject</strong></TableCell>
                        <TableCell align="right"><strong>Score</strong></TableCell>
                        <TableCell align="right"><strong>Max Score</strong></TableCell>
                        <TableCell align="right"><strong>Percentage</strong></TableCell>
                        <TableCell align="center"><strong>CBC Grade</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {verificationData.performance.subjects.map((subject, index) => {
                        const subjectCBCGrade = getCBCGrade(subject.percentage);
                        return (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                            <TableCell sx={{ fontWeight: 'medium' }}>{subject.subject}</TableCell>
                            <TableCell align="right">{subject.score}</TableCell>
                            <TableCell align="right">{subject.max_score}</TableCell>
                            <TableCell align="right">{subject.percentage.toFixed(1)}%</TableCell>
                            <TableCell align="center">
                              <Tooltip title={subjectCBCGrade.description}>
                                <Chip
                                  icon={getCBCGradeIcon(subjectCBCGrade.grade)}
                                  label={subjectCBCGrade.grade}
                                  color={subjectCBCGrade.color}
                                  size="small"
                                  variant="filled"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* CBC Grading Legend */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>CBC Grading System</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Chip
                      icon={<StarIcon />}
                      label="EE - Exceeding Expectations (80%+)"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="ME - Meeting Expectations (70-79%)"
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<TrendingUpIcon />}
                      label="AE - Approaching Expectations (60-69%)"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<TrendingDownIcon />}
                      label="BE - Below Expectations (<60%)"
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Slide>

        {/* Verification Details */}
        <Slide direction="up" in timeout={800}>
          <Box>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'success.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold">Verification Details</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                  <Box sx={{ flex: { xs: 1, md: 1 } }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Report Generated</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(verificationData.report_info.generated_at)}</Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ flex: { xs: 1, md: 1 } }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">Verified At</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(verificationData.verification.verified_at)}</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">Verification Token</Typography>
                    <Tooltip title="Copy Token">
                      <IconButton onClick={() => copyToClipboard(verificationData.report_info.token)} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace" 
                    sx={{ 
                      bgcolor: 'grey.100', 
                      p: 2, 
                      borderRadius: 1, 
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}
                  >
                    {verificationData.report_info.token}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Slide>
      </Box>

      {/* Footer */}
      <Fade in timeout={1000}>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            This is an official verification of a student academic report generated by CVision.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            For any questions, please contact the school administration.
          </Typography>
        </Box>
      </Fade>
    </Container>
  );
};

export default ReportVerification; 